import React, { useEffect, useRef, useState } from "react";
import UserAvatar from "../../common/UserAvatar/UserAvatar";
import { uploadProfilePhoto } from "../../../api/profilePhotos";
import { executeQuery, executeMutation } from "../../../graphql/client";
import "./ProfilePhotoControl.css";

// Phase H. Self-service profile photo control. Reads the caller's
// current profileImgKey via the `me` query on mount, lets them pick a
// new file, then opens an inline crop UI -- circular preview window
// the user can pan + zoom -- before rendering a square 512x512 JPEG
// canvas blob and PUTting that to S3. The square output guarantees
// the photo fills the circle on every surface (UserAvatar, captain
// showcase, RSVPs viewer, etc.) without distortion.
//
// userId is the socialUsers DDB key, which for new signups matches
// the Cognito sub -- the caller passes it in. If the user has no
// socialUsers row yet, the mutation will surface a "User not found"
// error and the upload silently orphans on S3.

const ME_QUERY = `
  query MeForProfilePhoto {
    me {
      id
      email
      profileImgKey
    }
  }
`;

const SET_PROFILE_IMAGE = `
  mutation SetMyProfileImage($userId: ID!, $imgKey: String) {
    setUserProfileImage(userId: $userId, imgKey: $imgKey) {
      userId
      profileImgKey
    }
  }
`;

// Final canvas dimensions for the uploaded photo. 512x512 is plenty
// for any UserAvatar size we currently render (96 max) at retina
// density, and keeps the upload payload modest (~50-150 KB at 0.9
// JPEG quality).
const OUTPUT_SIZE = 512;
// Crop preview container is square; this is its side length in CSS
// pixels. The displayed circle is the same diameter (border-radius).
const PREVIEW_SIZE = 256;
const MIN_SOURCE_DIM = 200;
const MAX_ZOOM = 3;

const ProfilePhotoControl = ({ userId, email, name }) => {
  const fileInputRef = useRef(null);
  const [imgKey, setImgKey] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Crop session state. Populated when the user picks a file; cleared
  // when they confirm or cancel.
  const [cropImage, setCropImage] = useState(null); // HTMLImageElement
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await executeQuery(ME_QUERY, {});
        if (cancelled) return;
        const key = result?.me?.profileImgKey || null;
        setImgKey(key);
      } catch (err) {
        if (!cancelled) {
          // Non-fatal -- the initials avatar still renders.
          // eslint-disable-next-line no-console
          console.warn("Could not load profile image key:", err.message);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handlePick = () => {
    if (loading) return;
    fileInputRef.current && fileInputRef.current.click();
  };

  // Load the picked file into an HTMLImageElement so we can both
  // preview it and draw it onto a canvas for the cropped output.
  const handleFile = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ""; // reset so picking the same file twice re-fires
    if (!file) return;
    if (!userId) {
      setError("Can't find your user ID -- try signing out and back in.");
      return;
    }
    setError("");

    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      setError("Photo must be JPEG, PNG, or WEBP.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError("Photo is larger than 10 MB.");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        if (img.naturalWidth < MIN_SOURCE_DIM || img.naturalHeight < MIN_SOURCE_DIM) {
          setError(
            `Photo is too small (${img.naturalWidth}x${img.naturalHeight}). Need at least ${MIN_SOURCE_DIM}x${MIN_SOURCE_DIM}.`
          );
          return;
        }
        setCropImage(img);
        setScale(1);
        setOffset({ x: 0, y: 0 });
      };
      img.onerror = () => setError("Could not load that image.");
      img.src = reader.result;
    };
    reader.onerror = () => setError("Could not read that file.");
    reader.readAsDataURL(file);
  };

  // "cover" scale: smallest factor that makes the image fill the
  // preview square. Multiplied by the user's zoom slider for the
  // final displayed scale.
  const coverScale = cropImage
    ? Math.max(
        PREVIEW_SIZE / cropImage.naturalWidth,
        PREVIEW_SIZE / cropImage.naturalHeight
      )
    : 1;
  const displayScale = coverScale * scale;
  const drawnW = cropImage ? cropImage.naturalWidth * displayScale : 0;
  const drawnH = cropImage ? cropImage.naturalHeight * displayScale : 0;
  // Drawn top-left position inside the preview container, with the
  // image centered by default and shifted by the user's drag offset.
  const drawX = cropImage ? (PREVIEW_SIZE - drawnW) / 2 + offset.x : 0;
  const drawY = cropImage ? (PREVIEW_SIZE - drawnH) / 2 + offset.y : 0;

  // Pointer drag for panning the image within the circle. Clamping
  // offsets is intentionally loose -- we just keep the user from
  // dragging the image entirely out of view.
  const onPointerDown = (e) => {
    if (!cropImage) return;
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startOffsetX: offset.x,
      startOffsetY: offset.y,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    const { startX, startY, startOffsetX, startOffsetY } = dragRef.current;
    const dx = e.clientX - startX;
    const dy = e.clientY - startY;
    const maxOffset = PREVIEW_SIZE / 2; // keep at least the center pixel onscreen
    const nx = Math.max(-maxOffset, Math.min(maxOffset, startOffsetX + dx));
    const ny = Math.max(-maxOffset, Math.min(maxOffset, startOffsetY + dy));
    setOffset({ x: nx, y: ny });
  };
  const onPointerUp = (e) => {
    dragRef.current = null;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch (_) {
      // ignore
    }
  };

  // Render the current preview transform onto a 512x512 canvas and
  // get a JPEG blob. Scale factor from preview -> canvas is OUTPUT_SIZE
  // / PREVIEW_SIZE so the math stays trivial.
  const renderCanvasBlob = () =>
    new Promise((resolve, reject) => {
      if (!cropImage) return reject(new Error("No image loaded"));
      const canvas = document.createElement("canvas");
      canvas.width = OUTPUT_SIZE;
      canvas.height = OUTPUT_SIZE;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#fff"; // bg fill in case of transparent PNGs
      ctx.fillRect(0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
      const k = OUTPUT_SIZE / PREVIEW_SIZE;
      ctx.drawImage(
        cropImage,
        drawX * k,
        drawY * k,
        drawnW * k,
        drawnH * k
      );
      canvas.toBlob(
        (blob) => {
          if (!blob) return reject(new Error("Canvas export failed"));
          resolve(blob);
        },
        "image/jpeg",
        0.9
      );
    });

  const handleConfirmCrop = async () => {
    setError("");
    setLoading(true);
    try {
      const blob = await renderCanvasBlob();
      // Wrap as a File so uploadProfilePhoto's type/size validation passes.
      const file = new File([blob], "profile.jpg", { type: "image/jpeg" });
      const { s3Key } = await uploadProfilePhoto({ file, targetUserId: userId });
      const result = await executeMutation(SET_PROFILE_IMAGE, {
        userId,
        imgKey: s3Key,
      });
      const next = result?.setUserProfileImage?.profileImgKey || s3Key;
      setImgKey(next);
      setCropImage(null);
    } catch (err) {
      setError(err.message || "Upload failed");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelCrop = () => {
    setCropImage(null);
    setScale(1);
    setOffset({ x: 0, y: 0 });
    setError("");
  };

  const handleRemove = async () => {
    if (loading) return;
    if (!imgKey) return;
    if (!window.confirm("Remove your profile photo?")) return;
    setError("");
    setLoading(true);
    try {
      await executeMutation(SET_PROFILE_IMAGE, { userId, imgKey: null });
      setImgKey(null);
    } catch (err) {
      setError(err.message || "Remove failed");
    } finally {
      setLoading(false);
    }
  };

  // Crop mode: replace the avatar + buttons with the cropper.
  if (cropImage) {
    return (
      <div className="profile-photo-control profile-photo-control--cropping">
        <div
          className="profile-photo-control__crop"
          style={{ width: PREVIEW_SIZE, height: PREVIEW_SIZE }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          <img
            src={cropImage.src}
            alt="Crop preview"
            draggable={false}
            style={{
              position: "absolute",
              left: drawX,
              top: drawY,
              width: drawnW,
              height: drawnH,
              userSelect: "none",
              pointerEvents: "none",
            }}
          />
          <div className="profile-photo-control__crop-mask" />
        </div>
        <div className="profile-photo-control__actions">
          <label
            className="profile-photo-control__hint"
            style={{ display: "flex", alignItems: "center", gap: 8, width: "100%" }}
          >
            <span>Zoom</span>
            <input
              type="range"
              min="1"
              max={MAX_ZOOM}
              step="0.01"
              value={scale}
              onChange={(e) => setScale(parseFloat(e.target.value))}
              style={{ flex: 1, minWidth: 0 }}
            />
          </label>
          <p className="profile-photo-control__hint">
            Drag to pan, slider to zoom. The circle is what gets saved.
          </p>
          <div
            style={{
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
              justifyContent: "center",
              width: "100%",
            }}
          >
            <button
              type="button"
              className="profile-photo-control__btn profile-photo-control__btn--primary"
              onClick={handleConfirmCrop}
              disabled={loading}
              style={{ alignSelf: "auto" }}
            >
              {loading ? "Uploading…" : "Save photo"}
            </button>
            <button
              type="button"
              className="profile-photo-control__btn"
              onClick={handleCancelCrop}
              disabled={loading}
              style={{ alignSelf: "auto" }}
            >
              Cancel
            </button>
          </div>
          {error && <p className="profile-photo-control__error">{error}</p>}
        </div>
      </div>
    );
  }

  // Idle mode: current avatar + upload / replace / remove controls.
  return (
    <div className="profile-photo-control">
      <UserAvatar
        name={name}
        email={email}
        imgKey={imgKey}
        size={96}
        className="profile-photo-control__avatar"
      />
      <div className="profile-photo-control__actions">
        <button
          type="button"
          className="profile-photo-control__btn profile-photo-control__btn--primary"
          onClick={handlePick}
          disabled={loading}
        >
          {loading ? "Working…" : imgKey ? "Replace photo" : "Upload photo"}
        </button>
        {imgKey && (
          <button
            type="button"
            className="profile-photo-control__btn"
            onClick={handleRemove}
            disabled={loading}
          >
            Remove
          </button>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={handleFile}
          style={{ display: "none" }}
        />
        <p className="profile-photo-control__hint">
          JPEG, PNG, or WEBP — up to 10 MB. You'll crop to a circle before
          uploading. Shown next to your name on team and event pages.
        </p>
        {error && <p className="profile-photo-control__error">{error}</p>}
      </div>
    </div>
  );
};

export default ProfilePhotoControl;
